import React from "react";
import {
	SEGMENT_CMC,
	URL_CMC_AIRCRAFT_ACCIDENTS_AND_INCIDENTS_DATA,
	URL_CMC_AIRCRAFT_DATA,
	URL_CMC_AIRCRAFT_HISTORY_DATA,
	URL_CMC_AIRFRAME_MRO_EVENT_DATA,
	URL_DATA_FEEDS,
} from "../constants";
import { Hero } from "../sections/hero/Hero";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { ProductTabs } from "../sections/ProductTabs";
import { ProductSlider } from "../sections/ProductSlider";
import { Upgrades } from "../sections/Upgrades";
import { Trial } from "../sections/Trial";
import { Pricing } from "../sections/pricing/Pricing";
import { Faq } from "../sections/faq/Faq";
import { productSegmentPageProps } from "../../common/types/product";

export default function CommercialAircraftOwnershipData({
	packageName,
	subscribeLink,
	tabsNav,
	testimonials,
}: productSegmentPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="Access data on 2,900+ aircraft owners, lessors, SPVs, and trusts, including data on the aircraft lease-end dates and current market values."
				packages={[
					{
						label: SEGMENT_CMC,
						features: [
							"2,900+ aircraft owners tracked worldwide",
							"Get insights into detailed lessors’ portfolios, aircraft valuations and lease-end-dates",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			{tabsNav}
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Lessor’s Profile",
						description:
							"Get an overview of general lessor information, including:",
						list: [
							"IATA code",
							"Legal name",
							"Status (active or inactive)",
							"Legal Name",
							"Info (year when the lessor was founded)",
							"Stock market symbol (if available)",
							"Website",
							"Location",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-ownership-data/lessor-profile-overview.jpg",
						},
					},
					{
						title: "Portfolio",
						description:
							"List of all aircraft in the lessor’s portfolio by:",
						list: [
							"Aircraft type",
							"Owned aircraft and their current market values",
							"Managed aircraft and their current market values",
							"Total portfolio overview",
							"Share of each aircraft type within the total portfolio",
							"Average aircraft age",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-ownership-data/lessor-profile-portfolio.jpg",
						},
					},
					{
						title: "Orders",
						description:
							"Overview of all aircraft on order placed by lessor, sorted by:",
						list: ["Aircraft", "Engine type"],
						visual: {
							url: "/static/img/product/commercial-aircraft-ownership-data/lessor-profile-orders.jpg",
						},
					},
					{
						title: "SPVs",
						description:
							"List of all Special Purpose Vehicles for owned aircraft, including:",
						list: [
							"Number of aircraft",
							"Share in total portfolio",
							"Current market value",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-ownership-data/lessor-profile-spvs.jpg",
						},
					},
					{
						title: "Customers",
						description:
							"List of all customers and the number of aircraft leased, including:",
						list: [
							"Aircraft type info",
							"Share in total portfolio",
							"Current market values",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-ownership-data/lessor-profile-customers.jpg",
						},
					},
					{
						title: "Contacts",
						description:
							"List of all senior manager contacts within the lessor, including:",
						list: [
							"Their names",
							"Job titles",
							"Emails",
							"LinkedIn profiles",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-ownership-data/lessor-profile-contacts.jpg",
						},
					},
					{
						title: "News",
						description:
							"News articles published related to the specific lessor:",
						list: [
							"Latest news (all news in chronological order)",
							"General news (lessor legal disputes, operator vs. operator or lessor lawsuits with major implications, and more)",
							"Fleet news (dry lease contracts from lessors or other operators; lease terminations enforced by lessors or courts, and more)",
							"Management changes (new appointments, resignations, etc.)",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-ownership-data/lessor-profile-news.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search for aircraft ownership by lessor, including their aircraft portfolio, the engines powering those aircraft, or the aircraft register."
				slides={[
					{
						title: "Lessor",
						description: "Search for aircraft owners by location:",
						list: ["Continent", "Country", "Company name"],
						visual: {
							url: "/static/img/product/commercial-aircraft-ownership-data/lessor-search-by-country.jpg",
							title: "List of all Aircraft lessors in United Kingdom, Europe",
						},
					},
					{
						title: "Aircraft Portfolio",
						description: "Search for aircraft owners by:",
						list: [
							"Aircraft market group",
							"Aircraft manufacturer",
							"Aircraft family",
							"Aircraft type/model/variant",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-ownership-data/lessor-search-by-aircraft.jpg",
							title: "List of all Lessors with A350 in their portfolio",
						},
					},
					{
						title: "Engines",
						description: "Search for aircraft owners by:",
						list: ["Engine manufacturer", "Engine type/subtype"],
						visual: {
							url: "/static/img/product/commercial-aircraft-ownership-data/lessor-search-by-engines.jpg",
							title: "List of all Lessors managing aircraft powered by Pratt & Whitney engines, type PW JT8D",
						},
					},
					{
						title: "Various Filters",
						description: "Filter the results by:",
						list: [
							"Aircraft role",
							"Number of aircraft in portfolio",
							"Registration country",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-ownership-data/lessor-search-by-various-filters.jpg",
							title: "List of all Lessors with 5-10 cargo aircraft registered in the US",
						},
					},
					{
						title: "Ownership",
						description: "Search for aircraft by:",
						list: [
							"Owner",
							"Manager",
							"SPV/ trust",
							"Ownership type",
							"Operating lease end dates",
							"Current market value",
							"Market lease rates",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-ownership-data/lessor-search-by-ownership.jpg",
							title: "All aircraft owned by AerCap, on operating lease ending on 10/31/2025, including current market value, and operator info",
						},
					},
				]}
			/>
			<ProductTabs
				title="Additional features included"
				subtitle=""
				tabs={[
					{
						title: "Aircraft Valuations",
						description: (
							<>
								<p>
									Aircraft valuations estimate the current
									market worth of an aircraft based on factors
									such as age, condition, and market demand.
									These values, provided by Collateral
									Verifications, are expressed in U.S. Dollars
									and assume a half-time maintenance condition
									for the airframe and major components.
								</p>
								<p>Insights into:</p>
								<ul className="ticks ticks--small">
									<li>
										Current market value (estimated price an
										aircraft would sell for today under
										typical market conditions, based on
										demand, supply, and aircraft condition)
									</li>
									<li>
										Market lease rate (the current market
										operating lease rate being paid monthly
										by good credits based on the typical
										lease terms for the aircraft type, not
										including any type of maintenance
										reserves)
									</li>
								</ul>
							</>
						),
						visual: {
							url: "/static/img/product/commercial-aircraft-ownership-data/aircraft-search-by-aircraft-valuations.jpg",
							title: "Current Market Value and Market Lease rates of Avolon owned and managed aircraft",
						},
					},
					{
						title: "Lease-end dates",
						description: (
							<>
								<p>
									Lease end dates represent confirmed,
									estimated, and minimum confirmed lease end
									dates down to the aircraft serial number,
									visible for any aircraft on operating lease.
								</p>
								<ul className="ticks ticks--small">
									<li>
										Get ability to track expirations through
										2040
									</li>
									<li>
										Use for fleet planning, lessor analysis,
										and aircraft finance decisions.
									</li>
								</ul>
							</>
						),
						visual: {
							url: "/static/img/product/commercial-aircraft-ownership-data/aircraft-search-by-lease-end-dates.jpg",
							title: "List of Operating Lease End Dates of aircraft managed by Avolon",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Aircraft Data",
						content:
							"Get access to our extensive fleet database with over 82,000 aircraft currently being tracked",
						url: URL_CMC_AIRCRAFT_DATA,
					},
					{
						title: "Aircraft History Data",
						content:
							"Get access to full historical operator data down to an aircraft level.",
						url: URL_CMC_AIRCRAFT_HISTORY_DATA,
					},
					{
						title: "Aircraft Accidents and Incidents Data",
						content:
							"Get a detailed overview of a commercial aviation aircraft accident/incident on a tail level.",
						url: URL_CMC_AIRCRAFT_ACCIDENTS_AND_INCIDENTS_DATA,
					},
					{
						title: "Airframe MRO Events Data",
						content:
							"Get insights into over 50,000 airframe maintenance events worldwide.",
						url: URL_CMC_AIRFRAME_MRO_EVENT_DATA,
					},
				]}
			>
				<Trial subscribeLink={subscribeLink} />
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				list={[
					"2,900+ aircraft owners tracked worldwide",
					"Get insights into detailed lessors’ portfolios, aircraft valuations and lease-end-dates",
					"Regular updates to ensure up-to-date insights",
				]}
				subscribeLink={subscribeLink}
				downloadsDescription="The Commercial Aviation Aircraft Ownership Data is available through the Commercial Aviation Aircraft Data downloads and can be accessed instantly with unlimited downloads in Excel, CSV, or JSON formats."
			>
				<Trial subscribeLink={subscribeLink} />
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download this data set?",
						content:
							"The Commercial Aviation Aircraft Ownership Data is available through the Commercial Aviation Aircraft Data downloads and can be accessed instantly with unlimited downloads in Excel, CSV, or JSON formats.  For more information about data downloads, please contact our sales team.",
						linkUrl: "mailto:sales@ch-aviation.com",
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content: (
							<p>
								Yes, the Commercial Aviation Aircraft Ownership
								data can be purchased as a data feed for
								seamless integration into your own system.
								ch-aviation also provides full support for the
								integration process. To learn more about data
								feeds, click{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "Does ch-aviation have fleet list information for every Lessor and aircraft type?",
						content: (
							<p>
								ch-aviation is tracking over 2,900 different
								aircraft owners. We only provide fleet list
								information for a specific set of aircraft types
								but cover all relevant aircraft types in
								commercial aviation and business jets service
								today with the exception of some regional types.
								Click
								<a
									href="https://www.ch-aviation.com/faq/lessors "
									rel="noopener noreferrer"
								>
									&nbsp;here&nbsp;
								</a>
								to check the aircraft types covered.
							</p>
						),
					},
					{
						title: "How often is the data updated?",
						content:
							"The Commercial Aviation Aircraft Ownership Data is being updated weekly, ensuring you always have the latest information.",
					},
					{
						title: "What does SPV stand for?",
						content: "SPV stands for Special Purpose Vehicle.",
					},
				]}
			/>
		</>
	);
}
